import { useEnvironmentConfig } from "pages/Controls/hooks/environment.hooks";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const CHATWOOT_BASE_URL = process.env.REACT_APP_CHATWOOT_BASE_URL as string;

export type ChatwootUser = {
  name?: string;
  email?: string;
  avatar_url?: string;
  phone_number?: string;
  identifier_hash?: string;
};

type ChatwootSDK = {
  run(options: { websiteToken: string; baseUrl: string }): void;
};

type ChatwootInstance = {
  toggle: (state?: "open" | "close") => void;
  toggleBubbleVisibility: (state: "show" | "hide") => void;
  reset: () => void;
  setUser: (identifier: string, user: ChatwootUser) => void;
  setCustomAttributes: (attrs: Record<string, unknown>) => void;
  setLocale: (locale: string) => void;
};

declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>;
    chatwootSDK?: ChatwootSDK;
    $chatwoot?: ChatwootInstance;
  }
}

export type ChatwootContextType = {
  enabled: boolean;
  ready: boolean;
  show: () => void;
  hide: () => void;
  shutdown: () => void;
  identify: (identifier: string, user: ChatwootUser) => void;
  setCustomAttributes: (attrs: Record<string, unknown>) => void;
};

const noop = () => {};

export const ChatwootContext = createContext<ChatwootContextType>({
  enabled: false,
  ready: false,
  show: noop,
  hide: noop,
  shutdown: noop,
  identify: noop,
  setCustomAttributes: noop,
});

type Props = {
  children: ReactNode;
};

export const ChatwootProvider = ({ children }: Props) => {
  const { language } = useI18n();
  const { authState } = useAuth();
  const { config } = useEnvironmentConfig();
  const websiteToken = config?.chatwootToken;
  const enabled = Boolean(CHATWOOT_BASE_URL && websiteToken);
  const [ready, setReady] = useState(() => Boolean(window.$chatwoot));
  const [requested, setRequested] = useState(false);
  const openWhenReady = useRef(false);
  const signedIn = authState.status === AuthStatus.Authenticated;
  const shouldLoad = enabled && (signedIn || requested);

  useEffect(() => {
    if (!shouldLoad || !websiteToken) return;

    window.chatwootSettings = {
      position: "right",
      type: "standard",
      launcherTitle: "",
      hideMessageBubble: true,
      locale: language,
    };

    if (window.$chatwoot) {
      setReady(true);
      return;
    }

    const handleReady = () => setReady(true);
    window.addEventListener("chatwoot:ready", handleReady);

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-chatwoot-sdk="true"]',
    );
    if (existing) {
      return () => window.removeEventListener("chatwoot:ready", handleReady);
    }

    const script = document.createElement("script");
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.dataset.chatwootSdk = "true";
    script.onload = () => {
      window.chatwootSDK?.run({
        websiteToken,
        baseUrl: CHATWOOT_BASE_URL,
      });
    };
    document.head.appendChild(script);

    return () => window.removeEventListener("chatwoot:ready", handleReady);
  }, [shouldLoad, websiteToken]);

  useEffect(() => {
    if (!ready) return;
    window.$chatwoot?.setLocale(language);
  }, [ready, language]);

  useEffect(() => {
    if (!ready || !openWhenReady.current) return;
    openWhenReady.current = false;
    window.$chatwoot?.toggle("open");
  }, [ready]);

  const show = useCallback(() => {
    if (window.$chatwoot) {
      window.$chatwoot.toggle("open");
      return;
    }
    openWhenReady.current = true;
    setRequested(true);
  }, []);
  const hide = useCallback(() => window.$chatwoot?.toggle("close"), []);
  const shutdown = useCallback(() => window.$chatwoot?.reset(), []);
  const identify = useCallback(
    (identifier: string, user: ChatwootUser) =>
      window.$chatwoot?.setUser(identifier, user),
    [],
  );
  const setCustomAttributes = useCallback(
    (attrs: Record<string, unknown>) =>
      window.$chatwoot?.setCustomAttributes(attrs),
    [],
  );

  const contextValue = useMemo<ChatwootContextType>(
    () => ({
      enabled,
      ready,
      show,
      hide,
      shutdown,
      identify,
      setCustomAttributes,
    }),
    [enabled, ready, show, hide, shutdown, identify, setCustomAttributes],
  );

  return (
    <ChatwootContext.Provider value={contextValue}>
      {children}
    </ChatwootContext.Provider>
  );
};

export const useChatwoot = (): ChatwootContextType => {
  const context = useContext(ChatwootContext);
  if (!context) {
    throw new Error("useChatwoot must be used within a ChatwootProvider");
  }
  return context;
};
